import { InfoIcon, LoaderIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import { cn } from '@app/lib/cn'
import { formatCount, formatDesi, formatPercent } from './format'
import { LIVE_STRIP_WIDTH_MM, type EstimateResult, type MaterialEstimate } from './live-calc'

/**
 * Canlı tahmin — what this set costs, from what is on the screen right now.
 *
 * The card is pinned under the rail rather than folded into it because it is
 * the only thing here that answers the question the model room actually asks
 * ("kaç desi?"), and because it must be visible while the material selects are
 * being changed — that is the edit it responds to.
 *
 * It never claims to be the costing. The heading says kaydedilmemiş, every
 * figure is per pair, and the numbers move under the operator as they work;
 * what is stored, hashed and approved is still whatever the server computes
 * from the saved geometry.
 */

export function LiveCalcCard({
  estimate,
  pending,
  error,
  onShowLayout,
  className,
}: {
  estimate: EstimateResult | null
  pending: boolean
  error: string | null
  /**
   * Opens the Yerleşim view on this material's strip — the drawn form of the
   * numbers on the row. Only offered when the estimate actually carries a
   * layout for the material; a link to an empty drawing teaches people not to
   * click it.
   */
  onShowLayout?: (materialId: number) => void
  className?: string
}) {
  const hasRows = estimate != null && estimate.materials.length > 0

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Canlı tahmin — kaydedilmemiş</CardTitle>
        {pending ? (
          <span className="flex items-center gap-1 text-micro text-ink-muted">
            <LoaderIcon className="size-3 animate-spin" aria-hidden />
            hesaplanıyor…
          </span>
        ) : (
          <span className="text-micro text-ink-muted">
            {formatCount(LIVE_STRIP_WIDTH_MM)} mm şerit · çift başına
          </span>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-2">
        {error ? <p className="text-data text-hardal">{error}</p> : null}

        {!hasRows && !error ? (
          <p className="text-data text-ink-muted">
            {pending
              ? 'Parçaların yerleşimi hesaplanıyor.'
              : 'Parçalara malzeme seçin; tüketim burada çıkar.'}
          </p>
        ) : null}

        {hasRows ? (
          <ul className={cn('flex flex-col gap-2', pending && 'opacity-60')}>
            {estimate.materials.map((material) => (
              <MaterialRow
                key={material.materialId}
                material={material}
                onShowLayout={onShowLayout}
              />
            ))}
          </ul>
        ) : null}

        {estimate && estimate.unassigned.pieceCount > 0 ? (
          <p className="flex items-start gap-1.5 text-micro text-hardal">
            <InfoIcon className="mt-px size-3 shrink-0" aria-hidden />
            {formatCount(estimate.unassigned.pieceCount)} parçada malzeme seçilmedi —{' '}
            {formatDesi(estimate.unassigned.netDesi)} desi hiçbir toplama girmiyor.
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

function MaterialRow({
  material,
  onShowLayout,
}: {
  material: MaterialEstimate
  onShowLayout?: ((materialId: number) => void) | undefined
}) {
  return (
    <li className="flex flex-col gap-0.5 border-b border-hairline pb-2 last:border-b-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-data font-medium text-ink">
          {material.materialName}
        </span>
        <span className="shrink-0 font-mono text-data tnum text-ink">
          {formatDesi(material.consumptionDesi ?? material.netDesi)}
          <span className="text-micro text-ink-muted"> desi/çift</span>
        </span>
      </div>

      <dl className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 font-mono text-micro tnum text-ink-muted">
        <Figure label="net" value={formatDesi(material.netDesi)} />
        <Figure label="ölçek" value={formatDesi(material.scaleDesi)} />
        {material.wasteInclusiveDesi != null ? (
          <Figure label="fire dahil" value={formatDesi(material.wasteInclusiveDesi)} />
        ) : null}
        <Figure label="verim" value={formatPercent(material.efficiency)} />
        <Figure
          label="parça"
          value={`${formatCount(material.pieceCount)} · ${formatCount(material.instanceCount)} adet`}
        />
      </dl>

      {onShowLayout && material.layout ? (
        <button
          type="button"
          onClick={() => onShowLayout(material.materialId)}
          className="self-start text-micro text-celik hover:underline"
        >
          Yerleşimi gör →
        </button>
      ) : null}

      {material.problem ? <p className="text-micro text-hardal">{material.problem}</p> : null}
    </li>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1">
      {/* Full-strength ink-muted keeps the 11px label above AA in the dark theme. */}
      <dt className="font-sans text-ink-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  )
}
